/**
 * SC-739 — les questions posées juste avant de valider : couverts, sauce,
 * emballage. Elles portent sur la commande entière, pas sur un plat, d'où leur
 * place ici et non dans la fiche produit.
 *
 * Rien n'est coché par défaut : c'est ce qui garde les couverts en opt-in, comme
 * l'impose la loi AGEC (couverts jetables uniquement sur demande).
 *
 * Les réponses sont des <button role="radio|checkbox"> et non des <input> :
 * un vrai bouton radio déjà coché n'émet plus d'événement quand on le reclique,
 * donc le client ne pouvait plus retirer sa réponse à une question facultative.
 *
 * SC-748 — une question peut n'être posée qu'au-delà d'un montant : le filtrage
 * est fait en amont (`groupsForAmount`), et une question qui apparaît en cours
 * de route se déplie au lieu de surgir sous le doigt du client.
 *
 * SC-760 — c'est une étape numérotée comme les autres, et « obligatoire » est
 * une pastille pleine, pas une parenthèse grise : sans réponse, ce bloc est ce
 * qui grise « Passer la commande », donc il doit se voir avant le bouton. Les
 * réponses courtes tiennent sur deux colonnes — une ligne pleine largeur pour
 * « Oui » puis « Non » poussait la fin de la commande hors de l'écran.
 *
 * SC-1010 — ce site tourne sans preflight Tailwind : `border-2` seul laisse
 * `border-style: none` (le cercle radio était invisible, fond `bg-paper` sur
 * carte `bg-paper`) et le <ul> garde ses puces. D'où `border-solid` et
 * `list-none p-0 m-0` explicites.
 */
import { useTranslation } from 'react-i18next';
import { catalogName, useCatalogText } from '@/lib/localizeCatalog';
import { formatPrice } from '@/components/ui/Price';
import type { OptionGroup } from '@/types/menu';
import type { SelectedOption } from '@/types/cart';
import { cartOptionsTotalCents, selectedCount } from './cartOptions';
import { StepCard, type CheckoutStep } from './CheckoutSteps';

interface Props {
  groups: OptionGroup[];
  selections: SelectedOption[];
  onChange: (selection: SelectedOption) => void;
  disabled?: boolean;
  step: CheckoutStep;
  index?: number;
  /** SC-775 — l'assistant n'affiche qu'une étape ; voir `StepCard`. */
  hidden?: boolean;
  /** « Choisissez une réponse » seulement après une tentative : avant, la
   *  pastille « Obligatoire » suffit, et un message d'erreur sur une question
   *  que le client n'a pas encore lue ne fait que l'alarmer. */
  showMissing?: boolean;
}

/**
 * Deux colonnes seulement quand chaque réponse y tient : au-delà, un libellé
 * qui se casse en trois lignes dans une demi-colonne se lit plus mal qu'une
 * ligne pleine largeur.
 */
function columnsClass(group: OptionGroup): string {
  if (group.options.some((o) => o.photo)) return 'flex flex-col gap-2';
  const longest = Math.max(...group.options.map((o) => catalogName(o).length));
  if (longest <= 16) return 'grid grid-cols-2 gap-2';
  if (longest <= 30) return 'grid gap-2 sm:grid-cols-2';
  return 'flex flex-col gap-2';
}

export default function CartOptionsSection({
  groups,
  selections,
  onChange,
  disabled = false,
  step,
  index,
  hidden = false,
  showMissing = true,
}: Props) {
  const { t } = useTranslation();
  const ct = useCatalogText();
  if (groups.length === 0) return null;

  const selectionOf = (groupId: string) => selections.find((s) => s.groupId === groupId);
  const supplement = cartOptionsTotalCents(groups, selections);

  const toggle = (group: OptionGroup, optionId: string) => {
    if (disabled) return;
    const current = selectionOf(group.id);
    const chosen = current?.options ?? [];
    if (chosen.some((o) => o.id === optionId)) {
      onChange({ groupId: group.id, options: chosen.filter((o) => o.id !== optionId) });
      return;
    }
    const max = group.max ?? 1;
    // Une question à choix unique remplace la réponse ; au-delà, on refuse plutôt
    // que de retirer silencieusement une réponse que le client vient de cocher.
    if (max <= 1) {
      onChange({ groupId: group.id, options: [{ id: optionId, qty: 1 }] });
      return;
    }
    if (selectedCount(current) >= max) return;
    onChange({ groupId: group.id, options: [...chosen, { id: optionId, qty: 1 }] });
  };

  return (
    <StepCard
      step={step}
      index={index}
      hidden={hidden}
      testId="cart-options-section"
      headerAside={
        supplement > 0 ? (
          <span className="font-mono text-sm text-ink" data-testid="cart-options-supplement">
            {t('checkout.cartOptions.supplement', { amount: formatPrice(supplement) })}
          </span>
        ) : null
      }
    >
      <style>{`
        @keyframes cart-option-in {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cart-option-group { animation: cart-option-in 220ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        @media (prefers-reduced-motion: reduce) {
          .cart-option-group { animation: none; }
        }
      `}</style>

      <div className="flex flex-col gap-4">
        {groups.map((group) => {
          const selection = selectionOf(group.id);
          const max = group.max ?? 1;
          const multiple = max > 1;
          const required = (group.min ?? 0) >= 1;
          const missing = required && selectedCount(selection) < (group.min ?? 1);
          const full = multiple && selectedCount(selection) >= max;
          // « offert » ne dit quelque chose qu'à côté d'une réponse payante : sur
          // une question où tout est gratuit (couverts), il se répétait sur
          // chaque ligne.
          const anyPriced = group.options.some((o) => o.priceDelta > 0);
          return (
            <div
              key={group.id}
              data-testid={`cart-option-group-${group.id}`}
              className="cart-option-group"
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-mono text-sm font-bold">{ct.name(group)}</span>
                {required ? (
                  <span
                    className="rounded-btn-sm border-2 border-ink px-1.5 py-[1px] font-mono text-[11px] font-bold text-white"
                    style={{ background: 'var(--sale-red)' }}
                  >
                    {t('checkout.cartOptions.required')}
                  </span>
                ) : (
                  <span className="font-mono text-xs text-ink/60">
                    {t('checkout.cartOptions.optional')}
                  </span>
                )}
                {multiple && (
                  <span className="font-mono text-xs text-ink/60">
                    {t('checkout.cartOptions.upTo', { max })}
                  </span>
                )}
              </div>

              <ul
                className={`mt-2 list-none p-0 m-0 ${columnsClass(group)}`}
                role={multiple ? undefined : 'radiogroup'}
                aria-label={ct.name(group)}
              >
                {group.options.map((opt) => {
                  const checked = (selection?.options ?? []).some((o) => o.id === opt.id);
                  // Une réponse non cochée d'une question déjà pleine reste
                  // visible mais inerte : la masquer ferait disparaître un choix
                  // sous les doigts du client.
                  const blocked = !checked && full;
                  return (
                    <li key={opt.id}>
                      <button
                        type="button"
                        role={multiple ? 'checkbox' : 'radio'}
                        aria-checked={checked}
                        disabled={disabled || blocked}
                        onClick={() => toggle(group, opt.id)}
                        data-testid={`cart-option-${opt.id}`}
                        className={`flex h-full w-full items-center gap-2 border-2 border-solid px-2.5 py-2.5 text-start font-mono text-sm transition-colors ${
                          checked ? 'border-ink bg-mint/30' : 'border-ink/30 bg-paper'
                        } ${blocked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
                      >
                        <span
                          aria-hidden="true"
                          className={`flex h-5 w-5 flex-none items-center justify-center border-2 border-solid border-ink ${
                            multiple ? '' : 'rounded-full'
                          } ${checked ? 'bg-ink' : 'bg-paper'}`}
                        >
                          {checked && (
                            <span
                              className={`block h-2 w-2 bg-paper ${multiple ? '' : 'rounded-full'}`}
                            />
                          )}
                        </span>
                        {opt.photo && (
                          <img
                            src={opt.photo}
                            alt=""
                            loading="lazy"
                            data-testid={`cart-option-photo-${opt.id}`}
                            className="h-10 w-10 flex-none border border-ink/20 object-cover"
                          />
                        )}
                        <span className="min-w-0 flex-1 break-words">{ct.name(opt)}</span>
                        {opt.priceDelta > 0 ? (
                          <span className="flex-none font-mono text-xs font-bold text-ink">
                            + {formatPrice(opt.priceDelta)}
                          </span>
                        ) : (
                          anyPriced && (
                            <span className="flex-none text-[11px] italic text-ink/60">
                              {t('checkout.cartOptions.free')}
                            </span>
                          )
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {missing && showMissing && (
                <p
                  className="mt-2 font-mono text-xs font-bold text-sale-red"
                  data-testid={`cart-option-missing-${group.id}`}
                >
                  ⚠ {t('checkout.cartOptions.requiredMissing')}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </StepCard>
  );
}
